import { differenceInDays } from 'date-fns';
import { useMemo, ReactNode } from 'react';
import { XAxis } from 'recharts';
import { getMonthTicks } from '../ChartUtils';
import { MonthlyTicks } from './MonthlyTicks';

interface MonthlyXAxisProps {
  data: {
    timestamp: number;
    [key: string]: any;
  }[];
  yAxisWidth: number;
  xAxisHeight?: number;
  minimumDays?: number;
  domain?: [number, number];
  axisWidth: number;
  minimumPixelsPerDay?: number;
  // When provided, these exact tick timestamps override the internally computed
  // month ticks. Used to force two stacked charts onto an identical shared tick set.
  overrideTicks?: number[];
}

// https://github.com/recharts/recharts/issues/2788
// Recharts does not render custom components (even if they wrap Recharts components),
// So instead of being an actual component, this is a hook
export const useBuildMonthlyXAxis: (props: MonthlyXAxisProps) => {
  shouldRenderFallback: boolean;
  component: ReactNode;
} = ({
  data,
  yAxisWidth,
  xAxisHeight,
  minimumDays = 90,
  domain,
  axisWidth,
  minimumPixelsPerDay = 1.25,
  overrideTicks
}) => {
  const visibleData = useMemo(() => {
    if (!domain) {
      return data;
    }

    return data.filter(row => row.timestamp >= domain[0] && row.timestamp <= domain[1]);
  }, [data, domain]);

  const { earliestTimestamp, latestTimestamp, displayedDaysCount } = useMemo(() => {
    const earliest = Math.min(...visibleData.map(row => row.timestamp));
    const latest = Math.max(...visibleData.map(row => row.timestamp));
    return {
      earliestTimestamp: earliest,
      latestTimestamp: latest,
      displayedDaysCount: differenceInDays(latest, earliest) + 1
    };
  }, [visibleData]);

  const xAxisMonthlyTicks = useMemo(
    () =>
      getMonthTicks({
        data,
        dataKey: 'timestamp',
        start: earliestTimestamp,
        end: latestTimestamp
      }),
    [data, earliestTimestamp, latestTimestamp]
  );

  const avgPixelsPerDay = axisWidth / (displayedDaysCount - 1);
  const shouldRenderFallback =
    displayedDaysCount <= minimumDays || avgPixelsPerDay < minimumPixelsPerDay;

  return {
    shouldRenderFallback,
    component: (
      <XAxis
        dataKey='timestamp'
        type='number'
        scale='time'
        domain={[earliestTimestamp, latestTimestamp]}
        ticks={overrideTicks ?? xAxisMonthlyTicks}
        tickLine={false}
        interval={0}
        allowDataOverflow
        height={xAxisHeight}
        tick={props => (
          <MonthlyTicks
            rechartsProps={props as Parameters<typeof MonthlyTicks>[0]['rechartsProps']}
            yAxisWidth={yAxisWidth}
            numTotalDates={displayedDaysCount}
          />
        )}
      />
    )
  };
};
